import React, { useEffect, useState } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Typography, Box, Card, CardContent, CardActionArea, Grid,
  Chip, alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { searchArticles } from '../../utils/api';
import { Article } from '../../utils/types';

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const theme = useTheme();
  const [results, setResults] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const search = async () => {
      if (!query) return;
      setLoading(true);
      try {
        const data = await searchArticles(query);
        setResults(data);
      } catch (err) {
        console.error('Search failed', err);
      } finally {
        setLoading(false);
      }
    };
    search();
  }, [query]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SearchIcon color="primary" />
        <Typography variant="h4" fontWeight={700}>
          Search results for "{query}"
        </Typography>
      </Box>

      {loading ? (
        <Typography color="text.secondary">Searching...</Typography>
      ) : results.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No articles found matching your search.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try different keywords or browse our categories.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {results.map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.id}>
              <Card sx={{ height: '100%' }}>
                <CardActionArea component={RouterLink} to={`/article/${article.slug}`} sx={{ height: '100%' }}>
                  <CardContent>
                    <Chip
                      label={article.category}
                      size="small"
                      sx={{ mb: 1, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                    />
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {article.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {article.excerpt}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SearchResults;
